/**
 * Member info card.
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー詳細の基本情報カード (30日メトリクス + 前月戦闘力比をラベル表示)
 * @graph-connects none
 */

import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { formatPt } from '../../lib/format.js';
import type { MemberDetail } from '../../types/api.js';

/** ラベル風 (chip) で数値を表示する共通スタイル。
 * @graph-connects none */
const VALUE_CHIP =
  'inline-block rounded-md bg-[var(--color-bg-elevated)] px-2 py-0.5 text-sm font-medium';

/** @graph-connects none */
interface MemberInfoCardProps {
  member: MemberDetail;
  labels: {
    nickName: string;
    githubName: string;
    points30d: string;
    releaseCount30d: string;
    /** 前月戦闘力比 */
    prevMonthRatio: string;
  };
  /**
   * 戦闘力履歴 (`/combat-power/history`) 由来のサマリー指標。
   * 未取得時は省略可。
   */
  combat?: {
    /** 当月実績戦闘力 ÷ 前月実績戦闘力 */
    prevMonthRatio: number | null;
  };
}

/**
 * Member info card.
 * @graph-connects none
 */
export function MemberInfoCard({ member, labels, combat }: MemberInfoCardProps) {
  const prevRatio = combat?.prevMonthRatio ?? null;

  // 前月比は 1.0 を境に色分け (成長=success / 低下=error)
  const ratioColor =
    prevRatio === null
      ? 'text-[var(--color-text-primary)]'
      : prevRatio >= 1
        ? 'text-[var(--color-success)]'
        : 'text-[var(--color-error)]';

  return (
    <Card className="animate-content-reveal h-full">
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <p className="text-sm text-[var(--color-text-muted)]">{labels.nickName}</p>
            <p className="text-[var(--color-text-primary)] font-medium">{member.nickName ?? '-'}</p>
          </div>
          <div>
            <p className="text-sm text-[var(--color-text-muted)]">{labels.githubName}</p>
            <p className="text-[var(--color-text-primary)] font-medium">
              {member.githubName ?? '-'}
            </p>
          </div>
          <div>
            <p className="text-sm text-[var(--color-text-muted)]">{labels.points30d}</p>
            <span className={`${VALUE_CHIP} text-[var(--color-text-primary)]`}>
              {formatPt(member.points30d)}
            </span>
          </div>
          <div>
            <p className="text-sm text-[var(--color-text-muted)]">{labels.releaseCount30d}</p>
            <span className={`${VALUE_CHIP} text-[var(--color-text-primary)]`}>
              {member.releaseCount30d.toString()}
            </span>
          </div>
          <div>
            <p className="text-sm text-[var(--color-text-muted)]">{labels.prevMonthRatio}</p>
            <span className={`${VALUE_CHIP} ${ratioColor}`}>
              {prevRatio === null ? '-' : `${prevRatio.toFixed(2)}x`}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
